<ng-container *ngIf="(!(resultAccounts$ | async) || (resultAccounts$ | async)?.length) && !showError">
    <div class="content-pane no-padding">
        <!-- Data templates -->

        <ng-template #accountNumber let-account>
            <ng-container *ngIf="account">{{ account.accountNumber }}</ng-container>
            <egov-text-ui-placeholder *ngIf="!account">T000000-00000000000</egov-text-ui-placeholder>
        </ng-template>

        <ng-template #parcelOwner let-parcel>
            <ng-container *ngIf="parcel">{{ parcel.owner }}</ng-container>
            <egov-text-ui-placeholder *ngIf="!parcel" length="32" lengthThreshold="15"></egov-text-ui-placeholder>
        </ng-template>

        <ng-template #parcelAddress let-parcel let-unavailableText="unavailableText">
            <ng-container *ngIf="parcel">
                <ng-container *ngIf="parcel.hasAddress">
                    {{ parcel.address }}
                </ng-container>
                <span *ngIf="!parcel.hasAddress" class="not-available-text">
                    {{ unavailableText ? unavailableText : "N / A" }}
                </span>
            </ng-container>
            <egov-text-ui-placeholder *ngIf="!parcel" length="32" lengthThreshold="25"></egov-text-ui-placeholder>
        </ng-template>

        <ng-template #parcelZipCity let-parcel>
            <ng-container *ngIf="parcel">
                <ng-container *ngIf="parcel.hasZipCity">
                    {{ parcel.zipCity }}
                </ng-container>
                <span *ngIf="!parcel.hasZipCity" class="not-available-text"> N / A </span>
            </ng-container>
            <egov-text-ui-placeholder *ngIf="!parcel" length="9" lengthThreshold="5"></egov-text-ui-placeholder>
        </ng-template>

        <ng-template #actionButtons let-account let-parcel="parcel" let-responsive="responsive">
            <div class="action-buttons" (click)="$event.stopPropagation()">
                <ng-container *ngIf="account; else actionButtonsPlaceholders">
                    <app-icon-button
                        *ngIf="!responsive"
                        [title]="account.cartButtonTitle"
                        [disabled]="!account.addAllEnabled"
                        (click)="$event.stopPropagation(); toggleAccountInCart(account.accountNumber)"
                        [spinner]="account?.toggling"
                    >
                        {{ account.inCart ? "remove_shopping_cart" : "add_shopping_cart" }}
                        <app-warned *ngIf="account.addAllWarning"></app-warned>
                    </app-icon-button>
                    <app-icon-button [moreIconAtTheEnd]="true" [matMenuTriggerFor]="accountMenu" (click)="$event.stopPropagation()">
                        more_vert
                    </app-icon-button>
                    <mat-menu #accountMenu="matMenu" xPosition="before" overlapTrigger>
                        <button mat-menu-item [disabled]="!account.addAllEnabled" (click)="toggleAccountInCart(account.accountNumber)">
                            {{ !account.inCart ? "Add to Cart" : "Remove from Cart" }}
                            <app-warned *ngIf="account.addAllWarning"></app-warned>
                        </button>
                        <mat-divider></mat-divider>
                        <a mat-menu-item [href]="parcel?.appraiserURL" target="_blank" [disabled]="!parcel?.appraiserURL">
                            Appraiser Info&nbsp;
                            <mat-icon>open_in_new</mat-icon>
                        </a>
                        <a mat-menu-item [href]="parcel?.parcelMapURL" target="_blank" [disabled]="!parcel?.parcelMapURL">
                            Parcel Map&nbsp;
                            <mat-icon>open_in_new</mat-icon>
                        </a>
                        <mat-divider></mat-divider>
                        <button class="auto-draft" *ngIf="autoDraftEnabled$ | async" mat-menu-item (click)="showAutoDraftDialog(account.accountNumber)">
                            Auto-Draft
                        </button>
                        <button
                            class="subscriptions"
                            *ngIf="subscriptionsEnabled$ | async"
                            mat-menu-item
                            (click)="showSubscriptionsDialog(account.accountNumber)"
                        >
                            Subscriptions
                        </button>
                        <button mat-menu-item class="watch" *ngIf="(watchListsEnabled$ | async) === true" (click)="watchAccount(account.accountNumber)">
                            Watch
                        </button>
                    </mat-menu>
                </ng-container>
                <ng-template #actionButtonsPlaceholders class="action-buttons">
                    <app-icon-button *ngIf="!responsive" [placeholder]="true"></app-icon-button>
                    <app-icon-button [moreIconAtTheEnd]="true" [placeholder]="true"></app-icon-button>
                </ng-template>
            </div>
        </ng-template>

        <!-- Items -->

        <ng-container>
            <!-- Table (Desktop) -->

            <table
                *ngIf="(viewport.tablet$ | async) === false; else responsiveView"
                class="styled borderless results"
                matSort
                (matSortChange)="changeSort($event)"
                [matSortActive]="currentSort?.active"
                [matSortDirection]="currentSort?.direction"
            >
                <thead>
                    <tr>
                        <th mat-sort-header="account_number" class="nowrap">
                            <div class="content">Account #</div>
                        </th>
                        <th mat-sort-header="owner" class="owner">
                            <div class="content">Owner</div>
                        </th>
                        <th mat-sort-header="parcel_address" class="address">
                            <div class="content">Address</div>
                        </th>
                        <th class="numeric nowrap">
                            <div class="content">
                                <div mat-sort-header="amount_due" arrowPosition="before">Due</div>
                            </div>
                        </th>
                        <th class="right nowrap">
                            <div class="content"> Actions </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        *ngFor="let item of (pptStateLoaded$ | async) ? (resultItems$ | async) : nullAccounts; trackBy: resultsTrackBy"
                        [routerLink]="item && '/property-tax/bill/' + item!.account.accountNumber"
                        [egovClickOnKey]
                        class="clickable highlight-on-hover"
                    >
                        <td class="nowrap account">
                            <ng-container *ngTemplateOutlet="accountNumber; context: { $implicit: item?.account }"></ng-container>
                        </td>
                        <td class="owner">
                            <ng-container *ngTemplateOutlet="parcelOwner; context: { $implicit: item?.parcel }"></ng-container>
                        </td>
                        <td class="address">
                            <ng-container *ngTemplateOutlet="parcelAddress; context: { $implicit: item?.parcel }"></ng-container>
                        </td>
                        <td class="numeric nowrap">
                            <ppt-account-balance [accountNumber]="item?.account.accountNumber" [showYears]="true" [showDueWord]="false"></ppt-account-balance>
                        </td>
                        <td class="right nowrap align-middle">
                            <ng-container *ngTemplateOutlet="actionButtons; context: { $implicit: item?.account, parcel: item?.parcel }"></ng-container>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Responsive view -->

            <ng-template #responsiveView>
                <ol class="parcel-result-list">
                    <li
                        *ngFor="let item of resultItems$ | async; trackBy: resultsTrackBy"
                        class="listing-item parcel-result-item clickable highlight-on-hover"
                        [routerLink]="item && '/property-tax/bill/' + item!.account.accountNumber"
                    >
                        <div class="top">
                            <div class="left">
                                <h4 class="header-text title is-clickable">
                                    <ng-container
                                        *ngTemplateOutlet="
                                            parcelAddress;
                                            context: {
                                                $implicit: item?.parcel,
                                                unavailableText: '[Address unavailable]'
                                            }
                                        "
                                    ></ng-container>
                                </h4>
                            </div>
                            <div class="right">
                                <ng-container
                                    *ngTemplateOutlet="actionButtons; context: { $implicit: item?.account, parcel: item?.parcel, responsive: true }"
                                ></ng-container>
                            </div>
                            <p class="account-number">
                                <ng-container *ngTemplateOutlet="accountNumber; context: { $implicit: item?.account }"></ng-container>
                            </p>
                        </div>
                        <ng-template #responsiveDue>
                            <ppt-account-balance [accountNumber]="item?.account.accountNumber" [multiline]="false"></ppt-account-balance>
                        </ng-template>
                        <ng-container *ngTemplateOutlet="responsiveDue"></ng-container>
                        <div class="data-fields no-bottom-padding">
                            <div class="data-field" *ngIf="!item?.parcel || item.parcel?.owner">
                                <div class="label">
                                    <ng-container *ngIf="item?.parcel?.owner">Owner</ng-container>
                                    <egov-text-ui-placeholder *ngIf="!item?.parcel?.owner"> Owner </egov-text-ui-placeholder>
                                </div>
                                <div class="content">
                                    <ng-container *ngTemplateOutlet="parcelOwner; context: { $implicit: item?.parcel }"></ng-container>
                                </div>
                            </div>
                            <ng-container *ngTemplateOutlet="responsiveDue"></ng-container>
                        </div>
                    </li>
                </ol>
            </ng-template>
        </ng-container>
    </div>
    <app-load-more *ngIf="showLoadMore" label="Load more" (click)="loadMore()" [loading]="loadingMore" [disabled]="loadMoreDisabled"></app-load-more>
</ng-container>
<app-content-placeholder
    *ngIf="(resultAccounts$ | async) && !(resultAccounts$ | async)!.length"
    customMessage="No parcels found for this search."
></app-content-placeholder>
<app-content-placeholder
    *ngIf="showError"
    type="error"
    [showRetry]="showErrorRetry"
    (retry)="retry()"
    customMessage="Error retrieving parcels."
></app-content-placeholder>
